export const getValueColor = (value) => {
  if (!value || value === '') return 'black';
  const num = parseFloat(value);
  if (isNaN(num)) return 'black';
  if (num > 0) return 'red';
  if (num < 0) return 'green';
  return 'black';
};

export const getRateColor = (rate, nationalRate) => {
  if (rate === undefined || rate === null || rate === '' || 
    nationalRate === undefined || nationalRate === null || nationalRate === '') return 'black';
  const rateNum = parseFloat(rate);
  const nationalRateNum = parseFloat(nationalRate);
  if (isNaN(rateNum) || isNaN(nationalRateNum)) return 'black';
  if (rateNum > nationalRateNum) return 'red';
  if (rateNum < nationalRateNum) return 'green';
  return 'black';
};

export const getRateDiffColor = (value) => {
  if (value === 0) return 'green';
  if (!value || value === '') return 'black';
  const num = parseFloat(value);
  if (isNaN(num)) return 'black';
  if (num > 1) return 'red';
  if (num < 1) return 'green';
  return 'black';
};

export const scoreColors = [
  { value: 0, color: '#7e22ce' },    // purple
  { value: 10, color: '#b91c1c' },   // dark red
  { value: 20, color: '#ef4444' },   // red
  { value: 30, color: '#f97316' },   // orange
  { value: 40, color: '#fb923c' },   // light orange
  { value: 50, color: '#eab308' },   // yellow
  { value: 60, color: '#facc15' },   // light yellow
  { value: 70, color: '#22c55e' },   // light green
  { value: 80, color: '#16a34a' },   // medium green
  { value: 90, color: '#166534' },   // dark green
];

export const getScoreColor = (value) => {
  if (value === 'N/A' || value === null || value === undefined) {
    return 'var(--text-color)';
  }
  
  const scoreValue = Math.min(Math.max(value, 0), 100);
  const index = Math.floor(scoreValue / 10);
  return scoreColors[Math.min(index, scoreColors.length - 1)].color;
};

const totalColors = ['#c5d0e3', '#b3bfd6', '#a1aec9', '#8f9dbc', '#7d8caf', '#6b7ba2', '#596a95', '#475988', '#35486b', '#1a2c4e'];

const comparisonColors = {
  negative: [
    '#008000',  // darkest green
    '#269153',  // dark green
    '#4ca366',  // medium green
    '#73b479',  // medium-light green
    '#99c68c'   // light green
  ],
  neutral: '#c0c0c0',
  positive: [
    '#e6a3a3',  // lightest red
    '#d98080',  // light red
    '#cc6666',  // medium red
    '#b32d2d',  // dark red
    '#ff0000'   // darkest red
  ]
};

export const getMapLegendColors = (activeTab, min, max, nationalRate = null, range = null) => {
  if (activeTab === 'scores') {
    return scoreColors.map(({ color }) => {
      const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
      return `rgba(${r}, ${g}, ${b}, 0.8)`;
    });
  }

  const negativeColors = comparisonColors.negative.map(color => {
    const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  });
  
  const neutralColor = (() => {
    const [r, g, b] = comparisonColors.neutral.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  })();
  
  const positiveColors = comparisonColors.positive.map(color => {
    const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  });

  const diffMetrics = ['avg_diff', 'avg_diff_pct', 'count_vs_avg', 'trend_diff', 
                       'trend_diff_pct', 'count_vs_prev', 'rate_diff_pct'];
  
  if (diffMetrics.includes(range)) {
    if (max <= 0) return negativeColors;
    if (min >= 0) return positiveColors;
    if (min === 0 && max === 0) return [neutralColor]; 
    return [...negativeColors, neutralColor, ...positiveColors];
  }
  
  const rateComparisonMetrics = ['rate_diff', 'rate_vs_national_rate'];
  
  if (rateComparisonMetrics.includes(range)) {
    if (max <= 1) return negativeColors;
    if (min >= 1) return positiveColors;
    if (min === 1 && max === 1) return [neutralColor]; 
    return [...negativeColors, neutralColor, ...positiveColors];
  }
  
  if (range === 'rate') {
    const natRate = parseFloat(nationalRate);
    if (min > natRate) return positiveColors;
    if (max < natRate) return negativeColors;
    if (min === natRate && max === natRate) return [neutralColor]; 
    return [...negativeColors, neutralColor, ...positiveColors];
  }

  return totalColors.map(color => {
    const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  });
};

export const getMapColor = (value, min, max, activeTab, nationalRate = null, range = null) => {
  if (value === null || value === undefined || value === '' || isNaN(value)) {
    return 'rgba(192, 192, 192, 0.2)';
  }

  if (range === 'count_avg' || range === 'national_rate') {
    return 'var(--silver)';
  }
 
  if (activeTab === 'scores') {
    const scoreValue = parseFloat(value);
    return getScoreColor(scoreValue);
  }
 
  if (range === 'avg_diff' || range === 'avg_diff_pct' || range === 'count_vs_avg' ||
      range === 'trend_diff' || range === 'trend_diff_pct' || range === 'count_vs_prev' || range === 'rate_diff_pct') {
    const compareValue = parseFloat(value);
    if (compareValue === 0) return comparisonColors.neutral;
    if (compareValue === Infinity) return comparisonColors.positive[comparisonColors.positive.length - 1];
    if (compareValue < 0) {
      const normalizedValue = Math.abs(compareValue / min);
      const index = Math.floor(normalizedValue * (comparisonColors.negative.length - 1));
      return comparisonColors.negative[comparisonColors.negative.length - 1 - Math.min(index, comparisonColors.negative.length - 1)];
    }
    const normalizedValue = compareValue / max;
    const index = Math.floor(normalizedValue * (comparisonColors.positive.length - 1));
    return comparisonColors.positive[Math.min(index, comparisonColors.positive.length - 1)];
  }
 
  if (activeTab === 'rates') {
    const rateValue = parseFloat(value);
    
    if (range === 'rate_diff' || range === 'rate_vs_national_rate') {
      if (rateValue === 1) return comparisonColors.neutral;
      if (rateValue < 1) {
        const normalizedValue = (1 - rateValue) / (1 - min);
        const index = Math.floor(normalizedValue * (comparisonColors.negative.length - 1));
        return comparisonColors.negative[comparisonColors.negative.length - 1 - Math.min(index, comparisonColors.negative.length - 1)];
      }
      const normalizedValue = (rateValue - 1) / (max - 1);
      const index = Math.floor(normalizedValue * (comparisonColors.positive.length - 1));
      return comparisonColors.positive[Math.min(index, comparisonColors.positive.length - 1)];
    }
    
    if (nationalRate !== null) {
      const natRate = parseFloat(nationalRate);
      if (rateValue === natRate) return comparisonColors.neutral;
      if (rateValue < natRate) {
        const normalizedValue = (natRate - rateValue) / (natRate - min);
        const index = Math.floor(normalizedValue * (comparisonColors.negative.length - 1));
        return comparisonColors.negative[comparisonColors.negative.length - 1 - Math.min(index, comparisonColors.negative.length - 1)];
      }
      const normalizedValue = (rateValue - natRate) / (max - natRate);
      const index = Math.floor(normalizedValue * (comparisonColors.positive.length - 1));
      return comparisonColors.positive[Math.min(index, comparisonColors.positive.length - 1)];
    }
  }
 
  const normalizedValue = (parseFloat(value) - min) / (max - min);
  const index = Math.floor(normalizedValue * (totalColors.length - 1));
  return totalColors[Math.max(0, Math.min(totalColors.length - 1, index))];
};